import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import ClarityQuestion from "./ClarityQuestion";
import { track } from "../../lib/clarity";

const CAT_LABEL = { cash: "Cash Flow", tax: "Tax Efficiency", protection: "Protection", networth: "Net Worth", retirement: "Retirement", context: "About You", goals: "Your Priorities" };

const visibleQuestions = (questions, tier, answers) =>
  questions.filter((q) => {
    if (q.tier !== tier) return false;
    if (q.show_if && !Object.entries(q.show_if).every(([k, vals]) => vals.includes(answers[k]))) return false;
    if (q.type === "top_pick" && (answers[q.source] || []).length < 2) return false;
    return true;
  });

const isAnswered = (q, v) => {
  if (q.type === "bucket_mix") return v && q.buckets.every((b) => v[b.key]);
  if (q.type === "multi") return Array.isArray(v) && v.length > 0;
  return v != null && v !== "";
};

export default function ClarityFlow({ questions, tier, answers, setAnswers, onComplete, submitting }) {
  const list = useMemo(() => visibleQuestions(questions, tier, answers), [questions, tier, answers]);
  const [idx, setIdx] = useState(0);
  const advancing = useRef(false);
  const q = list[Math.min(idx, list.length - 1)];
  const tierAll = useMemo(() => questions.filter((x) => x.tier === tier), [questions, tier]);
  const total = tierAll.length;
  const pos = q ? tierAll.findIndex((x) => x.id === q.id) + 1 : 0;

  useEffect(() => { if (q) track("step_view", q.id); }, [q?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!q) return null;
  const value = answers[q.id];
  const last = idx >= list.length - 1;

  const finalize = (a) => {
    const goals = a.goals || [];
    const next = { ...a };
    if (goals.length === 1 || (next.top_goal && !goals.includes(next.top_goal))) next.top_goal = goals[0];
    if (!next.top_goal) delete next.top_goal;
    onComplete(next);
  };
  const next = (a = answers) => (last ? finalize(a) : setIdx((i) => i + 1));
  const set = (v) => {
    const a = { ...answers, [q.id]: v };
    setAnswers(a);
    if ((q.type === "single" || q.type === "top_pick") && !advancing.current) {
      advancing.current = true;
      setTimeout(() => {
        advancing.current = false;
        if (idx >= visibleQuestions(questions, tier, a).length - 1) finalize(a);
        else setIdx((i) => i + 1);
      }, 260);
    }
  };
  const skip = () => { const a = { ...answers }; delete a[q.id]; setAnswers(a); next(a); };

  return (
    <div data-testid={`clarity-flow-${tier}`} className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between text-xs text-exodus-muted mb-3">
        <span data-testid="clarity-progress-label" className="eyebrow">{CAT_LABEL[q.category]} · {pos} of {total}</span>
        <span>{Math.round((pos / total) * 100)}%</span>
      </div>
      <div className="h-1 bg-exodus-border mb-10" role="progressbar" aria-valuenow={pos} aria-valuemax={total}>
        <div data-testid="clarity-progress-bar" className="h-1 bg-exodus-gold" style={{ width: `${(pos / total) * 100}%`, transition: "width 400ms ease-out" }} />
      </div>

      <div key={q.id} className="rise">
        <h2 data-testid="clarity-question-prompt" className="font-display text-3xl sm:text-4xl leading-tight mb-3">{q.prompt}</h2>
        {q.help && <p className="text-sm text-exodus-muted mb-8 max-w-xl">{q.help}</p>}
        {!q.help && <div className="mb-8" />}
        <ClarityQuestion q={q} value={value} onChange={set} answers={answers} />
      </div>

      <div className="mt-10 flex items-center justify-between gap-4">
        <button type="button" data-testid="clarity-back-btn" disabled={idx === 0} onClick={() => setIdx((i) => Math.max(0, i - 1))}
          className="inline-flex items-center gap-2 text-sm text-exodus-muted hover:text-exodus-gold disabled:opacity-30 transition-colors">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="flex items-center gap-5">
          {q.id !== "goals" && (
            <button type="button" data-testid="clarity-skip-btn" onClick={skip} className="text-xs text-exodus-muted hover:text-exodus-text underline-offset-4 hover:underline">
              Skip
            </button>
          )}
          <button type="button" data-testid="clarity-next-btn" disabled={!isAnswered(q, value) || submitting} onClick={() => next()} className="btn-gold">
            {last ? (submitting ? "Scoring…" : "See my score") : "Next"} <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}
